// Extracted hooks from tableComponent per spec §4.3
import { useMemo, useRef, useEffect } from 'react';
import _ from 'lodash';
import type { TableComponentProps } from './types';

/**
 * Supplies the shared table's DOM ref and its loading skeleton rows, and tags the pagination size selector. Memoizes one placeholder object per page-size slot, recomputing only when the page size changes, while an effect copies props.paginationId onto the antd page-size search input whenever the data or that id changes.
 *
 * @param props - The table props, carrying the page size, the row data and the pagination element id.
 * @returns The table ref and the skeleton row placeholders.
 */
export function useTableComponent(props: TableComponentProps) {
  const tableRef = useRef<any>(null);

  /**
   * Computes the memoized skeleton row placeholders used while the table is loading. Maps a range of the page size into objects each carrying a sequential id, recomputing only when the page size changes.
   *
   * @returns An array of placeholder row objects keyed by id.
   */
  const skeletonData = useMemo(
    () =>
      _.map(_.range(props.pageSize ?? 0), (item) => ({
        id: item,
      })),
    [props.pageSize],
  );

  useEffect(() => {
    const select = tableRef.current?.querySelector('.ant-select-selection-search-input');
    if (select && props.paginationId) {
      select.id = props.paginationId;
    }
  }, [props.data, props.paginationId]);

  return { tableRef, skeletonData };
}
